<template>
  <b-card
    v-if="data"
    md="6"
    sm="6"
    no-body
    class="card-statistics"
  >
    <b-card-header>
      <b-card-title class="chart-title">
        {{ $t('Order Statistics') }}
        <tooltip-help text="Displays a summary of total orders processed and the cumulative delivery charges collected within the selected period." />
      </b-card-title>
      <b-card-text class="font-small-2 mr-25 mb-0" />
      <b-button
        v-if="showRefreshButton"
        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
        size="sm"
        class="widgetrefresh-button d-sm-flex align-items-center justify-content-center shadow-custom"
        :style="cardBorder"
        @click="refreshWidget"
      >
        <feather-icon
          icon="RefreshCwIcon"
          class="mr-50 refresh-icon"
        />
        <span class="align-middle refresh-span" />
        <time-ago
          class="timeAgo"
          :datetime="lastUpdatedAt"
          refresh
          locale="en"
          :long="longString"
          :style="{color: '#1C1F32'}"
        />
      </b-button>
    </b-card-header>
    <b-card-body class="statistics-body">
      <b-row>
        <b-col
          v-for="item in data"
          :key="item.icon"
          cols="12"
          md="6"
          :class="[ 'mt-md-0', 'mt-1']"
        >
          <b-media
            no-body
            class="stat-card"
            :style="{ backgroundColor: item.bgcolor}"
          >
            <b-media-body class="my-auto">
              <h4
                v-b-tooltip.hover.v-primary
                class="font-weight-bolder mb-1 stat-number"
                :title="item.subtitle === 'Orders Count' ? item.title.toLocaleString() : __numberWithCommas(item.title, true)"
              >
                {{ __numberWithCommas(smartFormatter(item.title)) }}
              </h4>
              <b-card-text class="font-small-3 mb-0 font-weight-bolder stat-subtitle">
                {{ $t(item.subtitle) }}
              </b-card-text>
            </b-media-body>
            <b-media-aside
              class="avatar-container"
            >
              <b-avatar
                size="40"
                :style="{ backgroundColor: item.color , color: item.iconColor }"
              >
                <feather-icon
                  size="24"
                  :icon="item.icon"
                />
              </b-avatar>
            </b-media-aside>
          </b-media>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardText,
  BCardBody,
  BRow,
  BCol,
  BMedia,
  BMediaAside,
  BAvatar,
  BMediaBody,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import { TimeAgo } from 'vue2-timeago'
import RefreshMixin from '@/@core/mixins/ui/refresh'
import TooltipHelp from './TooltipHelp.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    TimeAgo,
    BButton,
    TooltipHelp,
  },
  mixins: [RefreshMixin],
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
    showRefreshButton: {
      type: Boolean,
      default: true, // or false, depending on your default preference
    },
  },
  data() {
    return {
      refreshTimestamp: new Date(),
      longString: true,
    }
  },
  computed: {
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
      }
    },
  },
  methods: {
    refreshWidget() {
      // This is the original logic from your mixin
      this.$emit('refresh', this.queue)
      // This is the new logic to update the timestamp
      this.refreshTimestamp = new Date()
    },
    numberWithCommas(x, isFloat = false) {
      if (Number.isNaN(x)) return x
      const parts = parseFloat(x).toFixed(isFloat ? 2 : 0).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    },
    smartFormatter(value) {
      const abs = Math.abs(value)
      if (abs >= 1.0e9) {
        return `${(value / 1.0e9).toFixed(1)}B`
      }
      if (abs >= 1.0e6) {
        return `${(value / 1.0e6).toFixed(1)}M`
      }
      if (abs >= 1.0e3) {
        return `${(value / 1.0e3).toFixed(1)}K`
      }
      return value.toString()
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';
@media (max-width: 745px) {
  .card-statistics .col-12.col-md-6 {
    margin-top: 1rem;
    width: 100% !important;
  }
}
@media (min-width: 768px) and (max-width: 1024px) {
  .card-statistics .col-12.col-md-6 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 1rem;
  }
}
.stat-card {
  width: 100% !important;
  height: 110px;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  margin-left: 0;
}
.avatar-container {
  position: absolute; /* Position avatar absolutely */
  top: 16px;
  right: 16px;
}
.stat-number {
  font-size: 20px; /* Adjust the font size as needed */
}
.stat-subtitle {
  font-size: 14px !important; /* Adjust the font size as needed */
  color: #5e5873; /* Optional: Change color for better visibility */
}
.refresh-icon {
  color: #1C1F32;
}
</style>
